import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, AlertCircle, Loader2 } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getRoleByIdAsync } from '@/data/store/actions';
import { TRoleItem, TPermissionItem } from '@/data/types';
import { RoleSelection, PermissionsPanel } from './role';

interface RoleStepProps {
  email: string;
  onBack: () => void;
  onComplete: (selectedRole: TRoleItem | null, selectedPermissions: string[]) => void;
  isLoading?: boolean;
  error?: string | null;
}

const RoleStep: React.FC<RoleStepProps> = ({ 
  email, 
  onBack, 
  onComplete, 
  isLoading = false,
  error 
}) => {
  const dispatch = useAppDispatch();
  const [selectedRole, setSelectedRole] = useState<TRoleItem | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [hasSelectedRole, setHasSelectedRole] = useState(false);
  const [permissionsLocked, setPermissionsLocked] = useState(false);
  const [isLoadingRoleDetails, setIsLoadingRoleDetails] = useState(false);
  const [rolePermissions, setRolePermissions] = useState<TPermissionItem[]>([]);
  const [isRoleEditing, setIsRoleEditing] = useState(false);
  const [rolePermissionsCache, setRolePermissionsCache] = useState<Record<string, TPermissionItem[]>>({});

  // Fetch role details when selectedRole changes
  useEffect(() => {
    const fetchRoleDetails = async () => {
      if (!selectedRole) {
        // No role selected, unlock permissions
        setPermissionsLocked(false);
        setRolePermissions([]);
        return;
      }

      // Check if we already have cached permissions for this role
      if (rolePermissionsCache[selectedRole.id]) {
        const cachedPerms = rolePermissionsCache[selectedRole.id];
        setRolePermissions(cachedPerms);
        setPermissionsLocked(true);
        const permissionIds = cachedPerms.map((p: TPermissionItem) => p.id);
        setSelectedPermissions(permissionIds);
        return;
      }

      // Fetch role details from API to get full permissions
      setIsLoadingRoleDetails(true);
      try {
        const response = await dispatch(
          getRoleByIdAsync({
            roleId: selectedRole.id,
          })
        ).unwrap();

        if (response.items && response.items.length > 0) {
          const roleDetails = response.items[0];
          
          // Extract permissions from role details
          const rolePerms = roleDetails.permissions || [];
          setRolePermissions(rolePerms);

          // Cache the permissions for this role
          setRolePermissionsCache(prev => ({
            ...prev,
            [selectedRole.id]: rolePerms
          }));

          // Lock permissions and set them to role's permissions
          setPermissionsLocked(true);

          // Update selected permissions to match role permissions
          const permissionIds = rolePerms.map((p: TPermissionItem) => p.id);
          setSelectedPermissions(permissionIds);
        }
      } catch (error) {
        console.error("Failed to fetch role details:", error);
        setPermissionsLocked(false);
        setRolePermissions([]);
        // Keep the role selected but allow manual permission selection as fallback
      } finally {
        setIsLoadingRoleDetails(false);
      }
    };

    fetchRoleDetails();
  }, [selectedRole, dispatch]);

  const handleRoleSelect = useCallback((role: TRoleItem | null) => {
    setSelectedRole(role);
    
    if (role) {
      setHasSelectedRole(true);
      // The useEffect will handle fetching role details and setting permissions
    } else {
      // If no role selected, unlock permissions for custom selection
      setPermissionsLocked(false);
      setSelectedPermissions([]);
      setRolePermissions([]);
      // Keep hasSelectedRole true to maintain layout
    }
  }, []);

  const handlePermissionToggle = useCallback((permissionId: string) => {
    // Only allow toggling if permissions are not locked by role OR if we're editing a role
    if (permissionsLocked && !isRoleEditing) return;
    
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  }, [permissionsLocked, isRoleEditing]);

  const handleRoleEditingStateChange = useCallback((isEditing: boolean) => {
    setIsRoleEditing(isEditing);
  }, []);

  const handleComplete = () => {
    if (selectedRole || selectedPermissions.length > 0) {
      onComplete(selectedRole, selectedPermissions);
    }
  };

  const canComplete = selectedRole !== null || selectedPermissions.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto mt-8 max-w-7xl"
    >
      {/* Header */}
      <motion.div
        initial={false}
        animate={{
          opacity: hasSelectedRole ? 0 : 1,
          height: hasSelectedRole ? 0 : 'auto',
          marginBottom: hasSelectedRole ? 0 : 32,
        }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden text-center"
      >
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Επιλογή Ρόλου και Δικαιωμάτων</h2>
        <p className="text-gray-600">
          Για το χρήστη: <span className="font-medium text-blue-600">{email}</span>
        </p>
      </motion.div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-xl bg-red-50 border border-red-200 p-4"
        >
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-red-800 text-sm font-medium">Σφάλμα</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <motion.div
        initial={false}
        animate={{
          maxWidth: hasSelectedRole ? '100%' : '600px',
        }}
        transition={{ ease: [0.4, 0, 0.2, 1] }}
        className={`mx-auto flex min-h-[600px] transition-all duration-700 items-start ${
          hasSelectedRole ? 'justify-start' : 'justify-center'
        }`}
      >
        {/* Role Selection */}
        <RoleSelection 
          selectedRole={selectedRole} 
          onRoleSelect={handleRoleSelect} 
          hasSelectedRole={hasSelectedRole}
          onRoleEditingStateChange={handleRoleEditingStateChange}
          selectedPermissions={selectedPermissions}
        />

        {/* Permissions Panel */}
        <PermissionsPanel
          selectedRole={selectedRole}
          selectedPermissions={selectedPermissions}
          onPermissionToggle={handlePermissionToggle}
          isVisible={hasSelectedRole}
          permissionsLocked={permissionsLocked && !isRoleEditing}
          isLoadingRoleDetails={isLoadingRoleDetails}
          rolePermissions={rolePermissions}
          isRoleEditing={isRoleEditing}
        />
      </motion.div>

      {/* Actions */}
      <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-3 text-gray-600 transition-colors hover:text-gray-800 disabled:opacity-50"
        >
          <ArrowLeft className="h-5 w-5" />
          Πίσω
        </motion.button>

        <div className="flex items-center gap-4">
          {/* Selection Summary */}
          {(selectedRole || selectedPermissions.length > 0) && (
            <div className="text-sm text-gray-600">
              {selectedRole ? (
                <span>Ρόλος: <strong>{selectedRole.name}</strong></span>
              ) : (
                <span><strong>{selectedPermissions.length}</strong> προσαρμοσμένα δικαιώματα</span>
              )}
            </div>
          )}

          <motion.button
            whileHover={{ scale: canComplete && !isLoading ? 1.02 : 1 }}
            whileTap={{ scale: canComplete && !isLoading ? 0.98 : 1 }}
            onClick={handleComplete}
            disabled={!canComplete || isLoading}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium transition-all duration-200 ${
              canComplete && !isLoading
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            {isLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="h-5 w-5 rounded-full border-2 border-white border-t-transparent"
                />
                Αποστολή...
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                Αποστολή Πρόσκλησης
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default RoleStep;